// Company list + card + room blocks data layer (erp-core `/v1/erp/hotel/companies`,
// `…/companies/{id}` and `…/companies/{id}/terms` as built in 2A-17, and the list /
// blocks routes of 2A-22 — NOT built yet, so those shapes are the contract assumed in
// the 2B-12 report, "erp-core must serve (2A-22)"). Every figure is the server's
// (rule 7): the limit, due and remaining credit, the document counts, the block rows'
// folio totals and named counts. The client posts parsed rows and shows the answer.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useErp } from '@/app/erp-context';
import { erpApi } from '@/lib/api';
import { hotelKeys, type CompanyItem } from '../api';
import type { RoomingRow } from '../lib/rooming';

export interface CompanyTerms {
  customer_id: string;
  credit_limit_minor: number;
  terms_days: number;
  rate_plan_id?: string | null;
  /** unit_type_id → USD cents (the contract's fixed dollar price). */
  usd_fixed: Record<string, number>;
  contact_name: string;
  contact_phone: string;
}

/** The server's block words: «كتلة» · «مؤكَّد» · «في الفندق» · «مُحرَّرة» (the unnamed rooms went back to sale). */
export type BlockStatus = 'block' | 'confirmed' | 'in_house' | 'released';

export interface CompanyBlock {
  id: string;
  /** «وفد زيارة · دفعة ٣». */
  name: string;
  unit_type_id: string;
  /** Falls back to the loaded unit types by `unit_type_id` when the wire omits it (2A-17 as built). */
  unit_type_name?: string;
  /** The plural the wire carries for the type («أجنحة»); falls back to the name. */
  unit_type_plural?: string;
  qty: number;
  stay_from: string;
  stay_to: string;
  status: BlockStatus;
  /** Rooming-list rows with a guest. */
  named: number;
  /** When the unnamed rooms return to sale (a plain date: the arrival less the release hours). ALWAYS on the wire; `null` = no auto-release («بلا تحرير تلقائي»). */
  release_at: string | null;
  /** The company's main folio total for this block; null = none yet. */
  main_folio_minor?: number | null;
}

/** One row of `GET /hotel/companies?q=` (frontdesk) — the list behind «الوفود والشركات». */
export interface CompanyListItem extends CompanyItem {
  customer_id: string;
  name: string;
  phone: string;
  has_terms: boolean;
  credit_limit_minor: number;
  due_minor: number;
  open_folios_minor: number;
  remaining_minor: number;
  blocks_active: number;
}

export interface CompanyCard {
  customer_id: string;
  /** The company's own name (2A-22) — the card never needs the cashier-only receipt prefill. */
  name: string;
  has_terms: boolean;
  terms?: CompanyTerms;
  credit_limit_minor: number;
  due_minor: number;
  open_folios_minor: number;
  remaining_minor: number;
  open_documents: number;
  oldest_due_days: number;
  blocks_this_month: number;
  room_nights_this_month: number;
  contact_name: string;
  contact_phone: string;
  blocks: CompanyBlock[];
}

/** One rooming-list line as the server lists it. */
export interface BlockRow {
  seq: number;
  guest_name: string | null;
  /** Present only after check-in, masked by the server («جواز عراقي · ···567») — never the number; the rooming list takes no documents. */
  document_label?: string | null;
  unit_label?: string | null;
  arrived: boolean;
}

export interface BlockDetail extends CompanyBlock {
  rows: BlockRow[];
}

/** `POST /hotel/blocks`. */
export interface NewBlockBody {
  /** REQUIRED, one per submit (uuid v4): the same id with the same body answers the SAME block, so a retry never books twice. */
  request_id: string;
  company_customer_id: string;
  unit_type_id: string;
  qty: number;
  from: string;
  to: string;
  name?: string;
}

/** `PUT /hotel/companies/{id}/terms` (manager+) — the WHOLE terms object; `usd_fixed` is unit_type_id → USD cents ({} = no fixed dollar price). */
export interface CompanyTermsBody {
  credit_limit_minor: number;
  terms_days: number;
  rate_plan_id?: string;
  usd_fixed: Record<string, number>;
  contact_name: string;
  contact_phone: string;
}

const enc = encodeURIComponent;

export const companiesApi = {
  list: (q: string) => erpApi.get<{ items: CompanyListItem[] }>('/hotel/companies', q ? { q } : undefined),
  card: (customerId: string) => erpApi.get<CompanyCard>(`/hotel/companies/${enc(customerId)}`),
  putTerms: (customerId: string, body: CompanyTermsBody) => erpApi.put<CompanyTerms>(`/hotel/companies/${enc(customerId)}/terms`, body),
  block: (blockId: string) => erpApi.get<BlockDetail>(`/hotel/blocks/${enc(blockId)}`),
  createBlock: (body: NewBlockBody) => erpApi.post<CompanyBlock>('/hotel/blocks', body),
  roomingList: (blockId: string, rows: RoomingRow[]) => erpApi.post<BlockDetail>(`/hotel/blocks/${enc(blockId)}/rooming-list`, { rows }),
};

function useBranchKey() {
  const { me } = useErp();
  return me.branch?.id ?? null;
}

export function useCompanyCard(customerId: string) {
  const branch = useBranchKey();
  return useQuery({ queryKey: hotelKeys.resource('company', branch, customerId), queryFn: () => companiesApi.card(customerId),
    // the previous company's figures never stand under another company's URL
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[3] === customerId ? prev : undefined),
    retry: 1,
  });
}

/** The ONE read of `GET /hotel/companies?q=` (the list screen, the reservation panel's picker and the split screen) under the ONE key. */
export function useCompanies(q: string, enabled = true) {
  const branch = useBranchKey();
  const term = q.trim();
  return useQuery({ queryKey: hotelKeys.resource('companies', branch, term), queryFn: () => companiesApi.list(term), placeholderData: keepPreviousData, enabled, retry: 0 });
}

/** The company's terms are written whole; the card (and the list's credit figures) move with them. */
export function useSaveTerms(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CompanyTermsBody) => companiesApi.putTerms(customerId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hotelKeys.all }),
  });
}

export function useBlockDetail(blockId: string | null) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: hotelKeys.resource('block', branch, blockId),
    queryFn: () => companiesApi.block(blockId as string),
    enabled: !!blockId,
    placeholderData: keepPreviousData,
    retry: 1,
  });
}

export type BlockWrite = { kind: 'create'; body: NewBlockBody } | { kind: 'rooming'; blockId: string; rows: RoomingRow[] };

/** One mutation for every block write (create, rooming list): «one write at a time». */
export function useBlockWrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (w: BlockWrite) => (w.kind === 'create' ? companiesApi.createBlock(w.body) : companiesApi.roomingList(w.blockId, w.rows)),
    // the card's rows (named counts, statuses, release dates) and the list all move
    onSuccess: () => void qc.invalidateQueries({ queryKey: hotelKeys.all }),
  });
}
